import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PageHeader, DISCORD_URL, SEO, cardReveal, staggerContainer } from "@/components/shared";
import { useDiscordWidget, VoiceChips } from "@/components/DiscordWidget";

const STATUS = {
  online: { dot: "bg-emerald-400", label: "Online" },
  idle: { dot: "bg-amber-400", label: "Idle" },
  dnd: { dot: "bg-red-400", label: "Do not disturb" },
  offline: { dot: "bg-slate-500", label: "Offline" },
};

function MemberCard({ member }) {
  const status = STATUS[member.status] || STATUS.offline;
  const activity = member.game?.name ? `Playing ${member.game.name}` : status.label;
  return (
    <div
      data-testid={`discord-member-${member.id}`}
      className="group flex items-center gap-3 rounded-2xl border border-purple-500/15 bg-[#11131f]/80 p-4 transition-all duration-300 hover:border-purple-500/50 hover:-translate-y-1"
    >
      <div className="relative shrink-0">
        <img
          src={member.avatar_url}
          alt={member.username}
          className="w-11 h-11 rounded-full object-cover"
          loading="lazy"
        />
        <span
          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#11131f] ${status.dot}`}
        />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-white truncate">{member.username}</p>
        <p className="text-xs text-slate-400 truncate">{activity}</p>
      </div>
    </div>
  );
}

export default function DiscordPage() {
  const { data, isLoading } = useDiscordWidget();
  const members = data ? (data.members || []).filter((m) => m.avatar_url) : [];
  const count = data?.presence_count;

  return (
    <>
      <SEO
        title="Our Discord — Vexora Development"
        description="Meet the Vexora Development community live: see who is online right now, what they're playing, our voice channels, and join the server."
      />
      <PageHeader
        eyebrow="Community"
        title={
          <>
            One server.{" "}
            <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-purple-500 bg-clip-text text-transparent">
              Every builder.
            </span>
          </>
        }
        subtitle="Our Discord is the heart of Vexora Development — where requests are made, projects are born, and the community hangs out. Here's who's inside right now."
      />

      <section data-testid="discord-live-section" className="relative pb-24 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap items-center justify-between gap-4 mb-10"
          >
            <div className="flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.6)]" />
              </span>
              <p data-testid="discord-live-summary" className="text-sm font-medium text-slate-300">
                {data ? (
                  <>
                    {data.name}
                    <span className="text-slate-500"> — </span>
                    <span className="text-white font-semibold">
                      {typeof count === "number" ? count.toLocaleString() : "—"} online now
                    </span>
                  </>
                ) : isLoading ? (
                  "Connecting to Discord…"
                ) : (
                  "Live status unavailable — the widget may be offline"
                )}
              </p>
            </div>
            {data && (data.channels || []).length > 0 && (
              <VoiceChips channels={data.channels} size="sm" />
            )}
          </motion.div>

          {members.length > 0 ? (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            >
              {members.map((m) => (
                <motion.div key={m.id} variants={cardReveal}>
                  <MemberCard member={m} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            !isLoading && (
              <p className="text-sm text-slate-500">
                No members to show right now — the widget may be resting. Check back soon.
              </p>
            )
          )}

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.21, 0.65, 0.35, 1] }}
            className="relative overflow-hidden rounded-3xl border border-purple-500/25 bg-[#11131f] px-6 py-16 sm:px-12 text-center mt-16"
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at 50% 0%, rgba(147,51,234,0.25) 0%, rgba(9,10,15,0) 65%)",
              }}
            />
            <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />
            <div className="relative">
              <h2
                data-testid="discord-join-heading"
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
              >
                Join Us?
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-base sm:text-lg text-slate-300">
                Request a plugin, get server help, or just hang out — it all happens in our
                Discord.
              </p>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="discord-join-us-button"
                className="btn-glow mt-8 inline-flex items-center gap-2 rounded-xl bg-purple-600 hover:bg-purple-500 px-8 py-4 text-base font-semibold text-white shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_45px_rgba(168,85,247,0.75)]"
              >
                Join Us?
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
